import React from 'react'
import { useTranslation } from 'react-i18next'

import { Chip, Stack, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { BlueprintEventFilter } from '../helpers/blueprintEventRows'

interface BlueprintEventsFilterPillsProps {
  value: BlueprintEventFilter
  onChange: (value: BlueprintEventFilter) => void
  // Years the data spans, newest first — one pill each.
  years: number[]
}

const pillSx = (selected: boolean) => ({
  m: 0,
  // Same size as the platform toggle beside it (28px tall, 12px side padding, 0.75rem
  // text); the !important mirrors the theme's Chip label override it has to beat.
  height: 28,
  '& .MuiChip-label': { px: '12px !important', py: '0 !important', fontSize: '0.75rem' },
  borderRadius: borderRadiusDefault,
  ...(selected
    ? {
        bgcolor: 'secondary.main',
        borderColor: 'secondary.main',
        color: 'secondary.contrastText',
        // MUI's outlined-clickable chip repaints the fill on hover/focus with
        // `&.MuiChip-clickable:hover` (and `&.Mui-focusVisible`) — a near-transparent
        // wash that out-ranks a plain `&:hover`, leaving the white label on a pale
        // fill. Match those selectors so the dark fill (and its contrast text) holds.
        '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible, &.Mui-focusVisible, &:focus-visible': {
          bgcolor: 'secondary.main',
          color: 'secondary.contrastText',
        },
      }
    : { bgcolor: 'background.paper' }),
})

/**
 * "Show:" segmented pills in the adopters table header — All (default), then
 * one pill per year the data spans. Filters the events of every expanded game
 * at once. Single-select; the active pill uses the dark secondary fill, the
 * rest are outlined.
 */
export const BlueprintEventsFilterPills: React.FC<BlueprintEventsFilterPillsProps> = React.memo(({ value, onChange, years }) => {
  const { t } = useTranslation()

  const options: { key: string; filter: BlueprintEventFilter; label: string }[] = [
    { key: 'all', filter: 'all', label: t('common:all') },
    ...years.map((year) => ({ key: String(year), filter: year as BlueprintEventFilter, label: String(year) })),
  ]

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      flexWrap="wrap"
      useFlexGap
      role="group"
      aria-label={t('market-analytics:event_table_filter_aria')}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
        {t('common:show')}:
      </Typography>
      {options.map((option) => {
        const selected = option.filter === value
        return (
          <Chip
            key={option.key}
            size="small"
            variant="outlined"
            clickable
            label={option.label}
            aria-pressed={selected}
            onClick={() => onChange(option.filter)}
            sx={pillSx(selected)}
          />
        )
      })}
    </Stack>
  )
})
BlueprintEventsFilterPills.displayName = 'BlueprintEventsFilterPills'
